import { Injectable, TemplateRef, inject } from '@angular/core';
import { Validators } from '@angular/forms';

import { Observable, filter, map, of } from 'rxjs';

import { CamAbstractInput, InputChoices, InputLabel, InputPanel, InputTextBox } from '@camelot/form-model';
import { extractEnum, isNonNullable } from '@camelot/utils';

import { Function, FunctionCreationPayload, FunctionModifierPayload } from '../dto/function';
import { MissionType } from '../dto/mission-type';
import { CamFunctionsService } from '../functions.service';

export enum EFunctionFormFields {
  name = 'name',
  roles = 'roleIds',
  missionTypes = 'missionTypes',
}

type OptionFields = {
  noLabel?: boolean;
  options$?: Observable<unknown>;
  visible$?: Observable<boolean>;
};

type Template = 'roleIds' | 'missionTypes';

@Injectable({
  providedIn: 'root',
})
export class CamFunctionsFormService {
  private _functionsService = inject(CamFunctionsService);

  public getFunctionForm(func?: Function | null) {
    return [
      new InputPanel({
        key: 'panel',
        contentClass: 'highlight-title g-space-md',
        children: [
          new InputTextBox({
            key: EFunctionFormFields.name,
            value: func?.name,
            label: 'user.functions.form.name',
            validators: [Validators.required],
          }),
          new InputChoices({
            key: EFunctionFormFields.roles,
            label: 'user.functions.form.roles',
            class: 'pt-xxl',
            withSearch: true,
            options: this._functionsService.fetchRoles$().pipe(
              filter(isNonNullable),
              map(roles =>
                roles.map(role => ({
                  id: role.id,
                  name: role.name,
                  data: role,
                }))
              )
            ),
            value: func?.roles?.map(x => x.id),
            multiple: true,
          }),
          new InputChoices({
            key: EFunctionFormFields.missionTypes,
            label: 'user.functions.form.mission-types',
            value: func?.missionTypes.map(x => x.toString()) ?? [],
            multiple: true,
            withSearch: true,
            options: of(
              extractEnum(MissionType, true).map(item => ({
                id: item.value.toString(),
                name: `user.mission-type.${item.name.toLowerCase()}`,
                data: item,
              }))
            ),
          }),
        ],
      }),
    ];
  }
  public formatCreationFunctionForm(data: any): FunctionCreationPayload {
    return {
      name: data[EFunctionFormFields.name],
      roleIds: data[EFunctionFormFields.roles],
      missionTypes: data[EFunctionFormFields.missionTypes]
        ? data[EFunctionFormFields.missionTypes].map((x: string) => Number(x))
        : [],
    };
  }

  public formatUpdateFunctionForm(id: string, data: any): Partial<FunctionModifierPayload> {
    return {
      id: id,
      name: data[EFunctionFormFields.name],
      roleIds: data[EFunctionFormFields.roles],
      missionTypes: data[EFunctionFormFields.missionTypes]
        ? data[EFunctionFormFields.missionTypes].map((x: string) => Number(x))
        : [],
    };
  }

  public getFields(
    fields: EFunctionFormFields,
    func: Function | null,
    template: { [key in Template]: TemplateRef<any> } | null,
    options?: OptionFields
  ): CamAbstractInput {
    switch (fields) {
      case EFunctionFormFields.name:
        return new InputTextBox({
          key: EFunctionFormFields.name,
          label: options?.noLabel === true ? '' : 'user.functions.form.name',
          validators: [Validators.required],
          value: func?.name ?? '',
        });
      case EFunctionFormFields.roles:
        return new InputChoices({
          key: EFunctionFormFields.roles,
          label: options?.noLabel === true ? '' : 'user.functions.form.roles',
          value: func?.roles?.map(role => role.id) ?? [],
          multiple: true,
          withSearch: true,
          options: this._functionsService.fetchRoles$().pipe(
            filter(isNonNullable),
            map(roles =>
              roles.map(role => ({
                id: role.id,
                name: role.name,
                data: role,
              }))
            )
          ),
          choiceTemplate: template ? { one: template[EFunctionFormFields.roles] } : {},
          visible$: options?.visible$ ?? of(true),
        });
      case EFunctionFormFields.missionTypes:
        return new InputChoices({
          key: EFunctionFormFields.missionTypes,
          label: options?.noLabel === true ? '' : 'user.functions.form.mission-types',
          value: func?.missionTypes.map(x => x.toString()) ?? [],
          multiple: true,
          withSearch: true,
          options: of(
            extractEnum(MissionType, true).map(item => ({
              id: item.value.toString(),
              name: `user.mission-type.${item.name.toLowerCase()}`,
              data: item,
            }))
          ),
          visible$: options?.visible$ ?? of(true),
        });
      default:
        return new InputLabel({
          key: 'nokey',
          label: '',
        });
    }
  }
}
